import { useCallback, useMemo } from 'react'

import { questionPositionToId } from '@aytm/app/stats/page/components/lib/utils'
import { svgToFile } from '@aytm/app/stats/lib/render/svg-export'
import { downloadExportFile, humanizedFiltersString } from '@aytm/app/stats/lib/stat-page'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

const BASE_EXPORT_OPTIONS = [
  { text: 'Download PNG image', key: 'png' },
  { text: 'Download JPEG image', key: 'jpeg' },
  { text: 'Download PDF document', key: 'pdf' },
  { text: 'Download SVG vector image', key: 'svg+xml' }
]

const isShowTurfExport = ({ features, qType, qSubType, answerVariants, batches }) => {
  const turfEnabled = !features?.turf?.disabled
  const authorizedExportEnabled = !features?.authorized_exports?.disabled

  if (!turfEnabled || !authorizedExportEnabled) return false

  if (qType === 'cb') {
    return answerVariants?.length > 1
  }

  // a checkbox matrix has TURF only in its radio form, as the Export tab's TURF list has it
  if (['mx', 'sr', 'se'].includes(qType) || ('mp' === qType && 'radios' === qSubType)) {
    return batches?.length > 1 && batches?.[0]?.answerVariants?.length > 2
  }

  return false
}

const getTurfExportOptions = ({ qType, batches }) => {
  const options = []

  if (qType === 'cb') {
    options.push({
      text: 'Download TURF (XLSX)',
      key: 'turf-answers',
      exportType: 'turf',
      turfType: 'answers'
    })
  } else if (['mx', 'sr', 'se', 'mp'].includes(qType)) {
    const answers = batches?.[0]?.answerVariants || []
    const len = answers.length

    if (len > 2) {
      options.push({
        text: `Download TURF TOP 1 A${len} (XLSX)`,
        key: `turf-top1-${len}`,
        exportType: 'turf',
        turfType: 'batches',
        turfMethod: 'top1',
        turfPositive: [len]
      })
    }

    if (len > 3) {
      options.push({
        text: `Download TURF TOP 2 A${len - 1}, A${len} (XLSX)`,
        key: `turf-top2-${len - 1}-${len}`,
        exportType: 'turf',
        turfType: 'batches',
        turfMethod: 'top2',
        turfPositive: [len - 1, len]
      })
      options.push({
        text: 'Download TURF TOP 2 A1, A2 (XLSX)',
        key: 'turf-top2-1-2',
        exportType: 'turf',
        turfType: 'batches',
        turfMethod: 'top2',
        turfPositive: [1, 2]
      })
    }

    if (len > 2) {
      options.push({
        text: 'Download TURF TOP 1 A1 (XLSX)',
        key: 'turf-top1-1',
        exportType: 'turf',
        turfType: 'batches',
        turfMethod: 'top1',
        turfPositive: [1]
      })
    }
  }

  return options
}

export const useExport = ({
  questionKey,
  chartRef,
  features = {},
  qType,
  qSubType,
  answerVariants = [],
  batches = [],
  // Charts whose pre-React menu differed can substitute their own list; see bpm.
  baseOptions = BASE_EXPORT_OPTIONS
}) => {
  const exportOptions = useMemo(() => {
    const turfOptions = isShowTurfExport({ features, qType, qSubType, answerVariants, batches })
      ? getTurfExportOptions({ qType, batches })
      : []

    return [...baseOptions, ...turfOptions]
  }, [features, qType, qSubType, answerVariants, batches, baseOptions])

  const formatSvgForExport = useCallback(chart => {
    if (!chart) return null

    const svg = chart.container.firstChild.cloneNode(true)

    svg.querySelectorAll('g.highcharts-button:not(.highcharts-breadcrumbs-button)').forEach(el => el.remove())

    const bg = svg.querySelector('rect.highcharts-background')
    if (bg) bg.setAttribute('fill', '#ffffff')

    return (
      svg.outerHTML
        .replace(/(url\(#highcharts-[0-9]+)"/g, '$1')
        .replace(/"/g, "'")
        // Keep the payload ASCII-only, but as XML character references rather than by
        // deleting the characters -- dropping them silently mangled accented brand and
        // attribute labels (bpm MDS / Topography being the worst hit).
        .replace(/[^\u0020-\u007E]/gu, ch => `&#${ch.codePointAt(0)};`)
    )
  }, [])

  const handleImageExport = useCallback(
    (fileType, callback) => {
      const chart = chartRef?.current?.chart
      if (!chart) {
        callback?.()
        return
      }

      const svgCode = formatSvgForExport(chart)

      svgToFile({
        file_type: fileType,
        svg: svgCode,
        position: questionKey
      })

      callback?.()
    },
    [chartRef, questionKey, formatSvgForExport]
  )

  const handleTurfExport = useCallback(
    (option, callback) => {
      const convertKey = key => {
        const reg = /vq(d)*/gm
        // @ts-ignore
        return (reg.test(key) ? key : questionPositionToId(Number(key)))?.toLowerCase()
      }

      const exportOptions = {
        extra: {
          qid: convertKey(questionKey),
          user: PageGlobals.USERNAME,
          type: option.turfType,
          filters: humanizedFiltersString(),
          customDomain: PageGlobals.SUBDOMAIN,
          ...(option.turfMethod && { method: option.turfMethod }),
          ...(option.turfPositive && { positive: option.turfPositive })
        }
      }

      downloadExportFile('binary', 'turf', { noCache: false }, exportOptions, callback)
    },
    [questionKey]
  )

  const handleExport = useCallback(
    (key, callback) => {
      const option = exportOptions.find(opt => opt.key === key)
      if (!option) {
        callback?.()
        return
      }

      if (key.startsWith('turf-')) {
        handleTurfExport(option, callback)
      } else {
        handleImageExport(key, callback)
      }
    },
    [exportOptions, handleImageExport, handleTurfExport]
  )

  return {
    exportOptions,
    handleExport
  }
}
